import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ShieldCheck, AlertTriangle, XOctagon, UserX, Gauge, Download, Mail, CalendarClock, Sparkles, Flame, CreditCard, FireExtinguisher } from "lucide-react";
import { api, useAuth, formatApiError, downloadFile, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import StatutBadge from "../components/StatutBadge";
import RapportDialog from "../components/RapportDialog";
import ActusPanel from "../components/ActusPanel";
import ExportDialog from "../components/ExportDialog";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const KPI_GROUPS = [
  {
    titre: "SST", color: "text-emerald-400", dot: "bg-emerald-500", testid: "kpi-group-sst", cols: "md:grid-cols-3 xl:grid-cols-6",
    items: [
      { key: "total", label: "Salariés suivis", icon: Users, color: "text-blue-400", bg: "bg-blue-950/60 border-blue-800", testid: "kpi-total", link: "/salaries" },
      { key: "valide", label: "SST valides", icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-950/60 border-emerald-800", testid: "kpi-valide", link: "/salaries?statut=valide" },
      { key: "a_recycler", label: "À recycler", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-950/60 border-amber-800", testid: "kpi-a-recycler", link: "/salaries?statut=a_recycler" },
      { key: "depasse", label: "Certificats dépassés", icon: XOctagon, color: "text-red-400", bg: "bg-red-950/60 border-red-800", testid: "kpi-depasse", link: "/salaries?statut=depasse" },
      { key: "non_forme", label: "Non formés", icon: UserX, color: "text-gray-400", bg: "bg-gray-800/60 border-gray-700", testid: "kpi-non-forme", link: "/salaries?statut=non_forme" },
      { key: "couverture", label: "Taux de couverture", icon: Gauge, color: "text-teal-400", bg: "bg-teal-950/60 border-teal-800", testid: "kpi-couverture", link: "/salaries" },
    ],
  },
  {
    titre: "SSIAP", color: "text-red-400", dot: "bg-red-500", testid: "kpi-group-ssiap", cols: "md:grid-cols-4",
    items: [
      { key: "ssiap_valide", label: "SSIAP valides", icon: Flame, color: "text-red-400", bg: "bg-red-950/40 border-red-900", testid: "kpi-ssiap-valide", link: "/ssiap" },
      { key: "ssiap_a_recycler", label: "SSIAP à recycler", icon: Flame, color: "text-red-400", bg: "bg-red-950/40 border-red-900", testid: "kpi-ssiap-recycler", link: "/ssiap" },
    ],
  },
  {
    titre: "Cartes Pro", color: "text-orange-400", dot: "bg-orange-500", testid: "kpi-group-cartes-pro", cols: "md:grid-cols-4",
    items: [
      { key: "cp_valide", label: "Cartes Pro valides", icon: CreditCard, color: "text-orange-400", bg: "bg-orange-950/40 border-orange-900", testid: "kpi-cartepro-valide", link: "/cartes-pro" },
      { key: "cp_a_recycler", label: "Cartes Pro à renouveler", icon: CreditCard, color: "text-orange-400", bg: "bg-orange-950/40 border-orange-900", testid: "kpi-cartepro-renouveler", link: "/cartes-pro" },
    ],
  },
  {
    titre: "Formations Incendie", color: "text-sky-400", dot: "bg-sky-500", testid: "kpi-group-incendie", cols: "md:grid-cols-3 xl:grid-cols-6",
    items: [
      { key: "fi_agents", label: "Personnes formées", icon: FireExtinguisher, color: "text-sky-400", bg: "bg-sky-950/40 border-sky-900", testid: "kpi-incendie-agents", link: "/formations-incendie" },
      { key: "fi_valide", label: "Formations valides", icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-950/60 border-emerald-800", testid: "kpi-incendie-valide", link: "/formations-incendie?statut=valide" },
      { key: "fi_a_recycler", label: "Formations à revoir", icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-950/60 border-amber-800", testid: "kpi-incendie-recycler", link: "/formations-incendie?statut=a_recycler" },
      { key: "fi_depasse", label: "Formations expirées", icon: XOctagon, color: "text-red-400", bg: "bg-red-950/60 border-red-800", testid: "kpi-incendie-depasse", link: "/formations-incendie?statut=depasse" },
      { key: "fi_non_forme", label: "Non formées", icon: UserX, color: "text-gray-400", bg: "bg-gray-800/60 border-gray-700", testid: "kpi-incendie-non-forme", link: "/formations-incendie?statut=non_forme" },
      { key: "fi_couverture", label: "Taux de couverture", icon: Gauge, color: "text-teal-400", bg: "bg-teal-950/60 border-teal-800", testid: "kpi-incendie-couverture", link: "/formations-incendie" },
    ],
  },
];

const CATS_SERVICE = [
  { key: "sst", label: "SST" },
  { key: "ssiap", label: "SSIAP" },
  { key: "carte_pro", label: "Cartes Pro" },
  { key: "incendie", label: "Incendie" },
];

const CAT_ACTIVE_STYLES = {
  sst: "border-emerald-500 bg-emerald-950/60 text-emerald-400",
  ssiap: "border-red-500 bg-red-950/60 text-red-400",
  carte_pro: "border-orange-500 bg-orange-950/60 text-orange-400",
  incendie: "border-sky-500 bg-sky-950/60 text-sky-400",
};

export default function Dashboard() {
  const { user } = useAuth();
  const canExport = canDo(user, "sst", "export");
  const canRappels = canDo(user, "rappels", "envoyer");
  const canWrite = canExport;
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);
  const [rapportOpen, setRapportOpen] = useState(false);
  const [catService, setCatService] = useState("sst");

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const testEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/test");
      toast.success(`Email envoyé à ${data.destinataire}`);
    } catch (e) {
      toast.error(formatApiError(e, "Échec de l'envoi"));
    } finally {
      setSending(false);
    }
  };

  if (!stats) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const kpiValue = (key) => {
    if (key === "total") return stats.total;
    if (key === "couverture") return `${stats.taux_couverture} %`;
    if (key === "ssiap_valide") return stats.ssiap?.valide ?? 0;
    if (key === "ssiap_a_recycler") return stats.ssiap?.a_recycler ?? 0;
    if (key === "cp_valide") return stats.carte_pro?.valide ?? 0;
    if (key === "cp_a_recycler") return stats.carte_pro?.a_recycler ?? 0;
    if (key === "fi_agents") return stats.incendie?.agents ?? 0;
    if (key === "fi_valide") return stats.incendie?.valide ?? 0;
    if (key === "fi_a_recycler") return stats.incendie?.a_recycler ?? 0;
    if (key === "fi_depasse") return stats.incendie?.depasse ?? 0;
    if (key === "fi_non_forme") return stats.incendie?.non_forme ?? 0;
    if (key === "fi_couverture") return `${stats.incendie?.couverture ?? 0} %`;
    return stats.counts[key];
  };

  const catServiceLabel = CATS_SERVICE.find((c) => c.key === catService)?.label || "SST";
  const svcData = stats.par_service_all?.[catService] || stats.par_service || [];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Vue d'ensemble</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Tableau de bord</h1>
          <p className="text-xs text-muted-foreground mt-1">Cliquez sur un indicateur pour voir les salariés concernés.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setRapportOpen(true)} data-testid="rapport-ia-button">
            <Sparkles className="h-4 w-4 mr-2" /> Synthèse IA
          </Button>
          {canWrite && (
            <Button variant="outline" onClick={testEmail} disabled={sending} data-testid="test-email-button">
              <Mail className="h-4 w-4 mr-2" /> {sending ? "Envoi..." : "Tester les rappels email"}
            </Button>
          )}
          {canExport && (
            <ExportDialog type="all" filename="export_complet.xlsx" testid="export-excel-button" variant="default" />
          )}
        </div>
      </div>
      <RapportDialog open={rapportOpen} onOpenChange={setRapportOpen} canGenerate={canWrite} />

      {KPI_GROUPS.map((g) => (
        <div key={g.titre} className="space-y-3" data-testid={g.testid}>
          <h2 className={`text-xs font-bold uppercase tracking-widest flex items-center gap-2 ${g.color}`}>
            <span className={`h-2 w-2 rounded-full ${g.dot}`} /> {g.titre}
          </h2>
          <div className={`grid grid-cols-2 gap-4 ${g.cols}`}>
            {g.items.map(({ key, label, icon: Icon, color, bg, testid, link }) => (
              <div key={key} onClick={() => navigate(link)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(link)}
                className={`rounded-xl border p-4 ${bg} cursor-pointer transition-all hover:-translate-y-0.5 hover:ring-1 hover:ring-primary/60`} data-testid={testid}>
                <div className="flex items-center justify-between mb-3">
                  <Icon className={`h-5 w-5 ${color}`} />
                </div>
                <p className="text-2xl sm:text-3xl font-extrabold font-mono" data-testid={`${testid}-value`}>{kpiValue(key)}</p>
                <p className="text-xs text-muted-foreground mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      <ActusPanel compact />

      <div className="flex flex-wrap items-center gap-2" data-testid="service-cat-selector">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mr-1">Vue par service :</span>
        {CATS_SERVICE.map((c) => (
          <button key={c.key} onClick={() => setCatService(c.key)} data-testid={`service-cat-${c.key}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${catService === c.key ? CAT_ACTIVE_STYLES[c.key] : "border-border text-muted-foreground hover:text-foreground"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6" data-testid="service-chart">
          <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "Outfit" }}>{catServiceLabel} par service</h2>
          <p className="text-xs text-muted-foreground mb-4">Répartition des statuts {catServiceLabel} par service du magasin</p>
          <ResponsiveContainer width="100%" height={Math.max(400, svcData.length * 32)}>
            <BarChart data={svcData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }} className="text-muted-foreground">
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.25} horizontal={false} />
              <XAxis type="number" tick={{ fill: "currentColor", fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="service" tick={{ fill: "currentColor", fontSize: 11 }} width={115} interval={0} />
              <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12, color: "hsl(var(--popover-foreground))" }} labelStyle={{ color: "hsl(var(--popover-foreground))" }} itemStyle={{ color: "hsl(var(--popover-foreground))" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="valide" name="Valide" stackId="a" fill="#059669" barSize={16} />
              <Bar dataKey="a_recycler" name="À recycler" stackId="a" fill="#D97706" />
              <Bar dataKey="depasse" name="Dépassé" stackId="a" fill="#DC2626" />
              <Bar dataKey="non_forme" name="Non formé" stackId="a" fill="hsl(var(--chart-nonforme))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-6" data-testid="expirations-panel">
          <div className="flex items-center gap-2 mb-1">
            <CalendarClock className="h-4 w-4 text-amber-400" />
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Prochaines personnes à recycler</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">{stats.sessions_planifiees} session(s) MAC planifiée(s)</p>
          <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
            {stats.personnes_a_recycler.length === 0 && (
              <p className="text-sm text-muted-foreground">Aucune personne à recycler actuellement.</p>
            )}
            {stats.personnes_a_recycler.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background/50 px-3 py-2" data-testid={`expiration-row-${e.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{e.prenom} {e.nom}</p>
                  <p className="text-xs text-muted-foreground">{e.service} · fin {e.fin_validite}</p>
                </div>
                <StatutBadge statut={e.statut} joursRestants={e.jours_restants} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6" data-testid="service-stats-table">
        <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "Outfit" }}>Détail par service — {catServiceLabel}</h2>
        <p className="text-xs text-muted-foreground mb-4">Nombre exact de {catServiceLabel} par service et statut</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3 text-emerald-400">Valides</th>
                <th className="px-4 py-3 text-amber-400">À recycler</th>
                <th className="px-4 py-3 text-red-400">Dépassés</th>
                <th className="px-4 py-3 text-gray-400">Non formés</th>
              </tr>
            </thead>
            <tbody>
              {svcData.map((s) => (
                <tr key={s.service} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`service-row-${s.service}`}>
                  <td className="px-4 py-2.5 font-medium">{s.service}</td>
                  <td className="px-4 py-2.5 font-mono">{s.total}</td>
                  <td className="px-4 py-2.5 font-mono text-emerald-400">{s.valide}</td>
                  <td className="px-4 py-2.5 font-mono text-amber-400">{s.a_recycler}</td>
                  <td className="px-4 py-2.5 font-mono text-red-400">{s.depasse}</td>
                  <td className="px-4 py-2.5 font-mono text-gray-400">{s.non_forme}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
